import i18n, { lngStorageKey } from '../i18n';
import { upload, createAdmin, editAdmin, getAdmins, getInterviews, getInterview, getReference, sendReference,
  loadLanguages, getUserReferences, updateInterview, makeDecision, getComplianceUpdatesById, getSlots, bookSlot,
  getAddressDetails } from '../api';
import * as actionTypes from './actionTypes';
import { getPagesCount } from '../helpers/general';
import { combineDateTime } from '../helpers/date';
import { USERS_LIST_PAGE_SIZE } from '../constants/general';

export function dispatchErrorAction (error, dispatch, forceShowErrorMessage = false) {
  if (error.response && error.response.status === 401 && !forceShowErrorMessage) {
    window.location.href = '/';
  } else {
    const message = error.response ? error.response.data.error.message : error.message;
    dispatch({ type: actionTypes.SHOW_ERROR, error: message });
    setTimeout(() => {
      dispatch({ type: actionTypes.HIDE_ERROR });
    }, 5000);
  }
}

export function dispatchGrassError (error, dispatch, forceShowErrorMessage = false) {
  if (error.response.status === 401 && !forceShowErrorMessage) {
    window.location.href = '/';
  } else {
    const { message } = error.response.data;
    dispatch({ type: actionTypes.SHOW_ERROR, error: message });
    setTimeout(() => {
      dispatch({ type: actionTypes.HIDE_ERROR });
    }, 5000);
  }
}

/*
*
* Users
*
* */

export function createAdminUser (userData) {
  return dispatch => createAdmin(userData)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function editAdminUser (id, data) {
  return dispatch => editAdmin(id, data)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function getAdminUsers (skip = 0) {
  return dispatch => getAdmins(skip)
    .then(({ data }) => {
      const adminsQuantity = data.find(admin => admin.count).count;
      const pagesQuantity = getPagesCount(adminsQuantity, USERS_LIST_PAGE_SIZE);
      const adminsList = data.filter(admin => admin.id); // to exclude entry with count
      dispatch({ type: actionTypes.GET_ADMINS_LIST_SUCCESS, adminsList, pagesQuantity });
      return ({ data: adminsList, pagesQuantity });
    })
    .catch(error => dispatchErrorAction(error, dispatch));
}

/*
*
* References
*
* */
export function getReferenceRequest (token) {
  return dispatch => getReference(token)
    .then(({ data }) => {
      const { language_id } = data;
      dispatch({ type: actionTypes.REFERENCE_REQUEST, payload: data });
      return loadLanguages(token)
        .then(locales => {
          const userLocale = locales.data.filter(lang => lang.id === language_id)[0];
          i18n.changeLanguage(userLocale.short);
          window.localStorage.setItem(lngStorageKey, userLocale.short);
          return data;
        })
        .catch(error => { dispatchErrorAction(error, dispatch); });
    })
    .catch(error => { dispatchErrorAction(error, dispatch); });
}

export function completeReference (formData) {
  return dispatch => sendReference(formData)
    .then(() => dispatch({ type: actionTypes.REFERENCE_SENT }))
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function getUserReferenceList (user_id) {
  return dispatch => getUserReferences(user_id)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function getLanguagesList () {
  return dispatch => loadLanguages()
    .then(data => {
      dispatch({ type: actionTypes.GET_LANGUAGES_LIST_SUCCESS, languages: data.data });
    })
    .catch(error => dispatchErrorAction(error, dispatch));
}

/**
 *
 * Checkr
 *
 */
export function makeDecisionAfterCheckr (compliance_id, decision) {
  return dispatch => makeDecision(compliance_id, decision)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function getComplianceUpdates (id) {
  return dispatch => getComplianceUpdatesById(id)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

/**
 *
 * Interviews
 *
 */
export function getAllInterviews () {
  return dispatch => {
    dispatch({ type: actionTypes.GET_INTERVIEWS_START });
    return getInterviews()
      .then(({ data }) => {
        let interviewsList = data;
        if (data.length) {
          const usersInterviews = data.filter(i => i.user_id);
          interviewsList = usersInterviews.map(int => {
            int.interview_date_time = combineDateTime(int.interview_date, int.interview_time);
            return int;
          });
        }
        dispatch({ type: actionTypes.GET_INTERVIEWS_SUCCESS, interviewsList });
        return interviewsList;
      })
      .catch(error => {
        dispatch({ type: actionTypes.GET_INTERVIEWS_ERROR, error });
      });
  };
}

export function getAllSlots (time_period = '') {
  return dispatch => getSlots(time_period)
    .then(({ data }) => dispatch({ type: actionTypes.GET_TIMESLOTS_SUCCESS, data }))
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function getWorkerInterview (workerId) {
  return dispatch => getInterview(workerId)
    .then(data => data.data.length ? data.data[0] : null)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function submitInterview (data) {
  return dispatch => updateInterview(data)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

export function submitInterviewSlot (data) {
  const hours = data.interview_hour > 9 ? '' + data.interview_hour : '' + data.interview_hour;
  const minutes = data.is_full_hour ? '00' : '30';
  const payload = {
    interview_date: data.interview_date,
    interview_time: `${hours}:${minutes}`
  };
  return dispatch => bookSlot(payload)
    .catch(error => dispatchErrorAction(error, dispatch));
}

/**
 *
 * Media files
 *
 * */

export function uploadFile (form, object_id, isCompany) {
  return dispatch => upload(form, object_id, isCompany)
    .then(data => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}

/*
*
* Helpers
*
* */

export function hideInfo () {
  return dispatch => {
    dispatch({ type: actionTypes.HIDE_ERROR });
    dispatch({ type: actionTypes.HIDE_INFO });
  };
}

export function toggleMobileAsideMenu () {
  return dispatch => dispatch({ type: actionTypes.TOGGLE_ASIDE_MENU });
}

export function closeMobileAsideMenu () {
  return dispatch => dispatch({ type: actionTypes.CLOSE_ASIDE_MENU });
}

export function hideAsideMenu () {
  return dispatch => dispatch({ type: actionTypes.HIDE_DESKTOP_ASIDE_MENU });
}

export function showAsideMenu () {
  return dispatch => dispatch({ type: actionTypes.SHOW_DESKTOP_ASIDE_MENU });
}

export function getGoogleAddressDetails (address) {
  return dispatch => getAddressDetails(address)
    .then(({ data }) => data)
    .catch(error => dispatchErrorAction(error, dispatch));
}



// WEBPACK FOOTER //
// ./src/actions/index.js